import React, { FC } from 'react';
import { useMutation } from '@apollo/client';
import { useTranslation } from 'react-i18next';
import { makeStyles, Theme } from '@material-ui/core/styles';
import FormControl from '@material-ui/core/FormControl';
import Button from '@material-ui/core/Button';
import TextField from '@material-ui/core/TextField';
import Typography from '@material-ui/core/Typography';
import DateFnsUtils from '@date-io/date-fns';
import { MuiPickersUtilsProvider, KeyboardDatePicker } from '@material-ui/pickers';
import VisibilityIcon from '@material-ui/icons/Visibility';
import RemoveIcon from '@material-ui/icons/Remove';
import { KeyTeacherCreatedActivityInput } from 'types/schema-generated-types';
import CREATE_KEY_TEACHER_CREATED_ACTIVITY_PREVIEW from 'graph/settings/key/create-key-teacher-created-activity-preview';
import useStore from 'store';
import { AlertVariant } from 'types/enums';

type CustomActivityProps = {
  errors?: { [key: string]: string } | null;
  id: string;
  onChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  onDateChange: (date: Date | null) => void;
  handleDeleteActivity: (id: string) => () => void;
  onBlur?: any;
  activityDisabled: boolean;
} & Pick<KeyTeacherCreatedActivityInput, 'date' | 'text' | 'title'>;

const useStyles = makeStyles((theme: Theme) => {
  return {
    container: {
      padding: theme.spacing(2),
      flexGrow: 1
    },
    label: {
      color: theme.palette.common.black,
      fontWeight: theme.typography.fontWeightBold,
      marginBottom: theme.spacing(1),
      marginLeft: theme.spacing(-1.3)
    },
    removeButton: {
      marginLeft: theme.spacing(1)
    }
  };
});

const CustomEnrichmentActivity: FC<CustomActivityProps> = ({
  errors,
  id,
  title,
  date,
  text,
  onChange,
  onDateChange,
  handleDeleteActivity,
  onBlur = () => undefined,
  activityDisabled = false
}) => {
  const [, { openAlert }] = useStore();
  const classes = useStyles();
  const { t } = useTranslation('EnrichmentSetting');
  const [createKeyTeacherCreatedActivityPreviewMutation] = useMutation(
    CREATE_KEY_TEACHER_CREATED_ACTIVITY_PREVIEW
  );

  const handleEnrichmentActivityPreviewClick = async (): Promise<void> => {
    try {
      const { data } = await createKeyTeacherCreatedActivityPreviewMutation({
        variables: { data: { title, text } }
      });

      if (data?.createKeyTeacherCreatedActivityPreview?.url) {
        window.open(data.createKeyTeacherCreatedActivityPreview.url, '_blank');
      }
    } catch (err) {
      openAlert({
        variant: AlertVariant.WARNING,
        text: t('Oops! Something went wrong while generating the preview.')
      });
    }
  };

  return (
    <FormControl component="fieldset" className={classes.container}>
      <Typography component="div">
        <TextField
          fullWidth
          size="small"
          variant="outlined"
          label={t('Activity Name')}
          value={title}
          name="title"
          onChange={onChange}
          placeholder=""
          error={!!errors?.title}
          helperText={errors?.title}
          onBlur={onBlur}
          disabled={activityDisabled}
        />
      </Typography>
      <Typography component="div">
        <MuiPickersUtilsProvider utils={DateFnsUtils}>
          <KeyboardDatePicker
            disableToolbar
            variant="inline"
            inputVariant="outlined"
            size="small"
            fullWidth
            format="MM/dd/yyyy"
            margin="normal"
            id="date-picker-inline"
            name="date"
            error={!!errors?.date}
            helperText={errors?.date}
            onBlur={onBlur}
            value={date}
            onChange={(newDate: Date | null): void => onDateChange(newDate)}
            label={t('Activity Date')}
            KeyboardButtonProps={{
              'aria-label': 'change date assessments'
            }}
            disabled={activityDisabled}
          />
        </MuiPickersUtilsProvider>
      </Typography>
      <Typography component="div">
        <TextField
          fullWidth
          size="small"
          variant="outlined"
          label={t('Activity Text')}
          placeholder=""
          multiline
          error={!!errors?.text}
          helperText={errors?.text}
          onBlur={onBlur}
          value={text}
          name="text"
          onChange={onChange}
          rowsMax={6}
          disabled={activityDisabled}
        />
        <Typography variant="caption" component="p">
          {t('800 Character Limit')}
        </Typography>
      </Typography>
      <Typography component="div">
        <Button
          variant="outlined"
          size="small"
          color="secondary"
          disabled={!title || !text}
          onClick={handleEnrichmentActivityPreviewClick}
          disableElevation
          startIcon={<VisibilityIcon />}
        >
          {t('Preview')}
        </Button>
        <Button
          variant="outlined"
          size="small"
          color="primary"
          onClick={handleDeleteActivity(id)}
          disableElevation
          className={classes.removeButton}
          startIcon={<RemoveIcon />}
          disabled={activityDisabled}
        >
          {t('Remove')}
        </Button>
      </Typography>
    </FormControl>
  );
};

export default CustomEnrichmentActivity;
